<template>
  <table class="full-width-table linked-contacts-table">
    <tbody>
      <tr class="table-header">
        <th style="width: 15%">Name</th>
        <th style="width: 20%">Company</th>
        <th style="width: 15%">Phone</th>
        <th class="email-header" style="width: 15%">
          Email
          <div class="email-checkbox header-checkbox" :class="{ 'show-checkbox': allEmailsChecked }">
            <Checkbox v-model="allEmailsChecked" @update:modelValue="toggleAllEmails" />
          </div>
        </th>
        <th style="width: 15%">Title</th>
        <th style="width: 20%">Department</th>
        <th style="width: 3rem"></th>
      </tr>
      <tr v-if="!loading && contacts.length > 0" v-for="contact in contacts" :key="contact.CONTACTID">
        <td>
          <span v-if="!contact.isCompanyEntry" class="hover-link" @click="navigateToContact(contact)">
            {{ contact.NA ? contact.DEPARTMENTS?.join(', ') || '' : ((contact.FIRSTNAME || '') + ' ' + (contact.LASTNAME || '')).trim() }}{{ contact.ISPRIMARY ? ' (Primary)' : '' }}
          </span>
        </td>
        <td>
          <span v-if="shouldMakeCompanyClickable(contact)" class="hover-link" @click="navigateToCompany(contact)">
            {{ getContactCompanyName(contact) }}
          </span>
          <span v-else>{{ getContactCompanyName(contact) }}</span>
        </td>
        <td>
          <span>{{ getContactPhone(contact) }}</span>
        </td>
        <td class="email-cell" v-if="contact.email?.ADDRESS && !contact.isCompanyEntry">
          <span class="hover-link" @click="openEmailClient(contact.email?.ADDRESS)">
            {{ contact.email.ADDRESS }}
          </span>
          <div class="email-checkbox" :class="{ 'show-checkbox': emailCheckboxes[contact.CONTACTID] }">
            <Checkbox v-model="emailCheckboxes[contact.CONTACTID]" />
          </div>
        </td>
        <td v-else></td>
        <td>
          <span>{{ contact.TITLES?.join(', ') || '' }}</span>
        </td>
        <td>
          <span>{{ contact.DEPARTMENTS?.join(', ') || '' }}</span>
        </td>
        <td>
          <div class="icon-button small-icon" @click="confirmUnlinkContact(contact)" title="Unlink Contact">
            <img id="noHover" src="../assets/unlink.svg" alt="Unlink" />
            <img id="hover" src="../assets/unlink-hover.svg" alt="Unlink" />
          </div>
        </td>
      </tr>
      <tr v-if="!loading && contacts.length === 0" class="no-results">
        <td colspan="7">No linked contacts found</td>
      </tr>
      <tr v-if="loading" class="loading-row">
        <td colspan="7"><img src="../assets/spinner.svg" /></td>
      </tr>
    </tbody>
  </table>

  <!-- Link Existing Contact Popup -->
  <LinkExistingContact v-if="showLinkExistingContact" :companyid="companyid" :contacts="contacts" @success="handleLinkSuccess" @cancel="showLinkExistingContact = false" />

  <!-- Unlink Confirmation -->
  <Confirmation
    v-if="showUnlinkConfirm"
    @confirm="handleUnlinkContact"
    @cancel="showUnlinkConfirm = false"
    header="Unlink Contact"
    :message="getUnlinkMessage()"
    confirmText="Unlink"
    cancelText="Cancel"
    :serious="true" />
</template>

<script>
import { ref, watch, computed } from 'vue';
import useCompanyContactsQuery from '@/composables/useCompanyContactsQuery';
import useContactMutate from '@/composables/useContactMutate';
import useLinkedContactMutate from '@/composables/useLinkedContactMutate';
import useDecryptSymbols from '@/composables/useDecryptSymbols';
import useAlerts from '@/composables/useAlerts';
import { phoneTypes } from '../composables/usePhoneTypes.js';
import Confirmation from './Confirmation.vue';
import LinkExistingContact from './LinkExistingContact.vue';
import Checkbox from './inputs/Checkbox.vue';

export default {
  name: 'LinkedContactsTab',
  components: {
    Confirmation,
    LinkExistingContact,
    Checkbox,
  },
  emits: ['emailCheckboxStateChanged'],
  props: {
    companyid: {
      type: String,
      required: true,
    },
  },
  setup(props, { emit }) {
    const { result, loading, refetch, companyName, allContacts } = useCompanyContactsQuery(props.companyid);
    const { updateContact } = useContactMutate();
    const { unlinkContact, unlinkCompany } = useLinkedContactMutate();
    const { decryptSymbols } = useDecryptSymbols();
    const { addAlert } = useAlerts();

    const contacts = ref([]);
    const showUnlinkConfirm = ref(false);
    const selectedContact = ref(null);
    const emailCheckboxes = ref({});
    const allEmailsChecked = ref(false);

    // Computed property to determine if all emails are checked
    const allEmailsCheckedComputed = computed(() => {
      const contactsWithEmail = contacts.value.filter((contact) => contact.email?.ADDRESS && !contact.isCompanyEntry);

      if (contactsWithEmail.length === 0) return false;

      return contactsWithEmail.every((contact) => emailCheckboxes.value[contact.CONTACTID] === true);
    });

    // Watch the computed property to update the ref
    watch(allEmailsCheckedComputed, (newValue) => {
      allEmailsChecked.value = newValue;
    });

    // Computed property to check if any emails are checked
    const hasCheckedEmails = computed(() => {
      return Object.values(emailCheckboxes.value).some((checked) => checked === true);
    });

    // Watch for changes in hasCheckedEmails to emit to parent
    watch(hasCheckedEmails, (newValue) => {
      emit('emailCheckboxStateChanged', newValue);
    });

    // Computed property to get checked email addresses
    const checkedEmailAddresses = computed(() => {
      const checkedContacts = contacts.value.filter((contact) => contact.email?.ADDRESS && !contact.isCompanyEntry && emailCheckboxes.value[contact.CONTACTID] === true);
      return checkedContacts.map((contact) => contact.email.ADDRESS);
    });

    const updateContacts = (contactsData) => {
      if (!contactsData) return;
      const decrypted = JSON.parse(JSON.stringify(contactsData)).map((contact) => decryptSymbols(contact));
      contacts.value = decrypted;

      // Initialize checkboxes for contacts with email addresses
      decrypted.forEach((contact) => {
        if (contact.email?.ADDRESS && !contact.isCompanyEntry) {
          if (!(contact.CONTACTID in emailCheckboxes.value)) {
            emailCheckboxes.value[contact.CONTACTID] = false;
          }
        }
      });
    };

    // Watch for changes in the combined contacts list
    watch(
      () => allContacts.value,
      (newContacts) => {
        updateContacts(newContacts);
        emit('emailCheckboxStateChanged', false);
      }
    );

    watch(
      () => props.companyid,
      () => {
        if (props.companyid) {
          refetch({ companyId: props.companyid });
        }
      }
    );

    // Method to copy checked emails to clipboard
    const copyCheckedEmails = () => {
      if (checkedEmailAddresses.value.length === 0) {
        addAlert('No emails selected', 'low', 3);
        return;
      }

      const emailString = checkedEmailAddresses.value.join('; ');

      // Create a temporary textarea element to copy the text
      const textarea = document.createElement('textarea');
      textarea.value = emailString;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      textarea.style.pointerEvents = 'none';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);

      addAlert(`${checkedEmailAddresses.value.length} email${checkedEmailAddresses.value.length > 1 ? 's' : ''} copied to clipboard`, 'low', 3);
    };

    // Method to open email client with checked emails
    const emailCheckedContacts = () => {
      if (checkedEmailAddresses.value.length === 0) {
        addAlert('No emails selected', 'low', 3);
        return;
      }

      const emailAddresses = checkedEmailAddresses.value.join(';');
      window.location.href = `mailto:${emailAddresses}`;
    };

    return {
      contacts,
      loading,
      refetch,
      updateContact,
      unlinkContact,
      unlinkCompany,
      addAlert,
      showUnlinkConfirm,
      selectedContact,
      companyName,
      emailCheckboxes,
      allEmailsChecked,
      hasCheckedEmails,
      checkedEmailAddresses,
      copyCheckedEmails,
      emailCheckedContacts,
    };
  },
  data() {
    return {
      showLinkExistingContact: false,
    };
  },
  methods: {
    getContactPhone(contact) {
      // Try direct phone first, then mobile phone
      const directPhone = contact.phones?.find((phone) => phone.TYPEID === phoneTypes.contact.direct);
      const mobilePhone = contact.phones?.find((phone) => phone.TYPEID === phoneTypes.contact.mobile);

      return directPhone?.NUMBERDISPLAY || mobilePhone?.NUMBERDISPLAY || '';
    },
    getContactCompanyName(contact) {
      // For company entries, use the company name
      if (contact.isCompanyEntry) {
        return contact.company?.NAME || '';
      }

      // For main company contacts, use the main company name
      if (contact.isMainCompany) {
        return this.companyName;
      }

      // For other contacts, use their linked company name
      return contact.company?.NAME || '';
    },
    navigateToContact(contact) {
      // Only navigate for actual contacts, not company entries
      if (!contact.isCompanyEntry) {
        this.$router.push({ name: 'contacts', params: { contactid: contact.CONTACTID } });
      }
    },
    shouldMakeCompanyClickable(contact) {
      // Don't make it clickable if it's the main company (same as current)
      if (contact.isMainCompany) {
        return false;
      }

      // Make it clickable if it's a company entry or a contact with a different company
      return (contact.isCompanyEntry && contact.company?.COMPANYID) || (contact.company?.COMPANYID && contact.company.COMPANYID !== this.companyid);
    },
    navigateToCompany(contact) {
      // Navigate to the company if it has a valid company ID
      if (contact.company?.COMPANYID) {
        this.$router.push({ name: 'companies', params: { companyid: contact.company.COMPANYID } });
      }
    },
    openEmailClient(emailAddress) {
      if (emailAddress) {
        window.location.href = `mailto:${emailAddress}`;
      }
    },
    confirmUnlinkContact(contact) {
      this.selectedContact = contact;
      this.showUnlinkConfirm = true;
    },
    getUnlinkMessage() {
      if (!this.selectedContact) return '';

      if (this.selectedContact.isCompanyEntry) {
        return `This will remove the link to ${this.selectedContact.company?.NAME}. Are you sure?`;
      }

      return `This will remove the company link from ${this.selectedContact.FIRSTNAME} ${this.selectedContact.LASTNAME}. Are you sure?`;
    },
    async handleUnlinkContact() {
      this.showUnlinkConfirm = false;

      if (!this.selectedContact) {
        return;
      }

      try {
        // Handle company entries differently than individual contacts
        if (this.selectedContact.isCompanyEntry) {
          // For company entries, unlink the company
          await this.unlinkCompany(this.companyid, this.selectedContact.company?.COMPANYID);
          this.addAlert('Company successfully unlinked', 'low', 2);
        } else {
          // For individual contacts, unlink the contact
          await this.unlinkContact(this.companyid, this.selectedContact.CONTACTID);
          this.addAlert('Contact successfully unlinked from company', 'low', 2);
        }

        // Refresh the contacts list
        this.refetch({ companyId: this.companyid });
      } catch (error) {
        console.error('Error unlinking contact from company:', error);
        this.addAlert('Error unlinking contact from company', 'high', 5);
      } finally {
        this.showUnlinkConfirm = false;
        this.selectedContact = null;
      }
    },
    showLinkExistingContactPopup() {
      this.showLinkExistingContact = true;
    },
    handleLinkSuccess() {
      // Close the popup and refresh the contacts list
      this.showLinkExistingContact = false;
      this.refetch({ companyId: this.companyid });
    },
    toggleAllEmails(checked) {
      // Get all contacts with email addresses
      const contactsWithEmail = this.contacts.filter((contact) => contact.email?.ADDRESS && !contact.isCompanyEntry);

      // Set all email checkboxes to the checked state
      contactsWithEmail.forEach((contact) => {
        this.emailCheckboxes[contact.CONTACTID] = checked;
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.linked-contacts-table {
  th:first-child,
  td:first-child {
    padding-left: 3.75rem;
  }

  th:last-child,
  td:last-child {
    padding-right: 1.4rem;
  }

  td {
    text-wrap: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .loading-row {
    td {
      background: white;
    }
  }
}

.hover-link {
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
}

.company-entry {
  font-weight: 600;
  font-style: italic;
  color: #666;
}

.email-cell {
  position: relative;
}

.email-checkbox {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  opacity: 0;
  transition: opacity 0.2s ease;
}

.email-cell:hover .email-checkbox {
  opacity: 1;
}

.email-checkbox.show-checkbox {
  opacity: 1;
}

.email-header {
  position: relative;
}

.header-checkbox {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  opacity: 0;
  transition: opacity 0.2s ease;
}

.email-header:hover .header-checkbox {
  opacity: 1;
}

.header-checkbox.show-checkbox {
  opacity: 1;
}
</style>
